import React, { useContext } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { ReactComponent as ExpiredFileIcon } from '@ubeya/shared-web/assets/compliance-expired.svg';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as BasePlusIcon } from '@ubeya/shared-web/assets/plus.svg';
import { ReactComponent as ApprovedFileIcon } from '@ubeya/shared-web/assets/compliance-approved-file.svg';
import { ReactComponent as PendingFileIcon } from '@ubeya/shared-web/assets/compliance-pending-file.svg';
import useFormattingMap from '@ubeya/shared-web/hooks/useFormattingMap';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  font-size: 14px;
  height: 100%;

  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $color }) => $color};

  &:hover {
    background-color: ${({ $hoverBackgroundColor }) => $hoverBackgroundColor};
    cursor: pointer;
  }
`;

const ApprovedWrapper = styled(Wrapper)`
  flex-direction: column;
  width: 100%;

  ${({ $hasExpiration }) =>
    $hasExpiration &&
    css`
      .expiration {
        display: none;
      }

      &:hover {
        .expiration {
          display: flex;
        }

        svg {
          width: 18px;
          height: 18px;
        }
      }
    `}
`;

const PendingWrapper = styled(Wrapper)`
  flex-direction: column;
  width: 100%;

  .expiration {
    display: flex;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ExpiredWrapper = styled(Wrapper)`
  flex-direction: column;
  width: 100%;

  .expiration {
    display: flex;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ExpirationDate = styled.div.attrs(() => ({ className: 'expiration' }))`
  color: ${({ theme, $approved }) => ($approved ? theme.colors.greenPositive : theme.colors.yellowPartially)};
  margin-top: 5px;
  font-size: 10px;
`;

const ExpirationText = styled.div`
  margin-top: 5px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.redNegative};
`;

const ExpiredFileRenderer = ({ t, theme, expirationDate, onClick }) => (
  <ExpiredWrapper
    onClick={onClick}
    $backgroundColor={`${theme.colors.redNegative}1A`}
    $color={theme.colors.redNegative}
    $hoverBackgroundColor={`${theme.colors.redNegative}40`}
    $hasExpiration={!!expirationDate}>
    <ExpiredFileIcon />

    <ExpirationText>{t('expired')}</ExpirationText>
  </ExpiredWrapper>
);

const ApprovedFileRenderer = ({ theme, t, formattingMap, expirationDate, onClick }) => (
  <ApprovedWrapper
    onClick={onClick}
    $backgroundColor={`${theme.colors.greenPositive}1A`}
    $hoverBackgroundColor={`${theme.colors.greenPositive}40`}
    $hasExpiration={!!expirationDate}>
    <ApprovedFileIcon />

    {!!expirationDate && (
      <ExpirationDate $approved>{`${t('expDate')} ${formattingMap.date(expirationDate)}`}</ExpirationDate>
    )}
  </ApprovedWrapper>
);

const PendingFileRenderer = ({ theme, expirationDate, t, formattingMap, onClick }) => (
  <PendingWrapper
    onClick={onClick}
    $backgroundColor={`${theme.colors.orangeChanges}1A`}
    $hoverBackgroundColor={`${theme.colors.orangeChanges}40`}>
    <PendingFileIcon />

    {!!expirationDate && <ExpirationDate>{`${t('expDate')} ${formattingMap.date(expirationDate)}`}</ExpirationDate>}
  </PendingWrapper>
);

const PlusIcon = styled(BasePlusIcon)`
  &:hover {
    cursor: pointer;
  }
`;

const GeneralAttachmentRenderer = ({ fieldData, setSelectedField, employee, isExpired, isAboutToExpire }) => {
  const { t } = useContext(ConfigContext);
  const theme = useTheme();
  const formattingMap = useFormattingMap();

  if (!fieldData) {
    return <></>;
  }

  const fileValueLink = fieldData?.value?.link;

  const onClick = () =>
    setSelectedField?.({
      ...fieldData,
      employee
    });

  if (!fileValueLink) {
    return (
      <Wrapper onClick={onClick} $hoverBackgroundColor={theme.colors.grey1}>
        <PlusIcon />
      </Wrapper>
    );
  }

  if (isExpired) {
    return (
      <ExpiredFileRenderer onClick={onClick} theme={theme} t={t} expirationDate={fieldData.value.expirationDate} />
    );
  }

  if (isAboutToExpire) {
    return (
      <PendingFileRenderer
        theme={theme}
        t={t}
        formattingMap={formattingMap}
        expirationDate={fieldData.value.expirationDate}
        onClick={onClick}
      />
    );
  }

  // TODO will be replaced with real condition
  const approvedFile = true;

  if (approvedFile) {
    return (
      <ApprovedFileRenderer
        theme={theme}
        formattingMap={formattingMap}
        expirationDate={fieldData.value.expirationDate}
        onClick={onClick}
        t={t}
      />
    );
  }

  return (
    <Wrapper $hoverBackgroundColor={theme.colors.grey1} onClick={onClick}>
      <PlusIcon />
    </Wrapper>
  );
};

export default GeneralAttachmentRenderer;
